import { Fragment } from 'react';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import LineChartSkeleton from '../../reusable/LineChart/LineChartSkeleton';
import { t } from '../../i18n/i18n';
import './AccountMetrics.scss';

/**
 * Placeholders for Account Metrics' sections while their own request is in
 * flight. The page draws from three endpoints and they land at different times,
 * so each section waits on its own rather than the page waiting on all three:
 *
 *   /account-metrics/accounts/<id>/   banner, risk status, recent trades,
 *                                     statistics, daily performance, account
 *                                     information, and the Advanced sections
 *                                     (which already take `loading`)
 *   /objectives-flat/<id>/            trading objectives
 *   /performance-chart/               equity curve
 *
 * Every piece renders the page's own layout classes — `am-banner`, `am-card`,
 * `am-trades__item`, `am-stats__cell`, `am-field` — so the box is already the
 * size it will be and only the contents are stand-ins. The banner alone is
 * 220px; a page that paints the cards first and is then pushed down by it is a
 * worse first paint than one that never moves.
 *
 * Daily Performance needs nothing here: its calendar is a fixed grid whose
 * geometry doesn't depend on the response, so it holds its own shape and only
 * the day tints arrive late.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

/** Card header: icon, title, and whatever the card hangs on the right. */
const HeadSkeleton = ({ right = '96px' }) => (
  <header className="am-card__head">
    <span className="am-card__title">
      <Skeleton w="14px" h={14} />
      <Skeleton w="104px" h={13} />
    </span>
    <Skeleton w={right} h={20} />
  </header>
);

/** The blue identity banner — 1:9051. */
export function BannerSkeleton() {
  return (
    <section className="am-banner" aria-busy="true" aria-label={t('Loading account')}>
      <Skeleton w="52px" h={52} light pill />
      <div className="am-banner__id">
        <Skeleton w="88px" h={11} light />
        <Skeleton w="132px" h={22} light />
        {/* The fact pills, so the strip does not resize as they arrive. */}
        <span className="am-banner__facts">
          <Skeleton w="86px" h={20} light pill />
          <Skeleton w="68px" h={20} light pill />
          <Skeleton w="62px" h={20} light pill />
        </span>
      </div>
    </section>
  );
}

/** Risk status — 1:9065. The pill keeps the card's amber wash behind it. */
export function RiskSkeleton() {
  return (
    <section className="am-risk" aria-busy="true" aria-label={t('Loading risk status')}>
      <div className="am-risk__left">
        <Skeleton w="72px" h={20} pill />
        <Skeleton w="48px" h={22} />
      </div>
      <Skeleton w="124px" h={27} pill />
    </section>
  );
}

/**
 * Equity curve — 1:9075.
 *
 * Share and the range tabs are bars rather than the live controls: a range the
 * trader can press before there is a curve to redraw would be claiming a state
 * the card can't honour yet.
 */
export function CurveSkeleton() {
  return (
    <div className="am-card am-curve" aria-busy="true" aria-label={t('Loading equity curve')}>
      <header className="am-card__head">
        <span className="am-card__title">
          <Skeleton w="14px" h={14} />
          <Skeleton w="82px" h={13} />
        </span>
        <div className="am-curve__controls">
          <Skeleton w="76px" h={32} />
          <Skeleton w="188px" h={32} />
        </div>
      </header>

      <div className="am-curve__summary">
        <Skeleton w="164px" h={34} />
        <Skeleton w="104px" h={13} />
      </div>

      <LineChartSkeleton className="am-curve__plot" height={384} showSecondaryLine />
    </div>
  );
}

/** Recent trades — six rows, the same slice the card renders. */
export function TradesSkeleton() {
  return (
    <div className="am-card am-trades" aria-busy="true" aria-label={t('Loading recent trades')}>
      <HeadSkeleton right="56px" />

      <ul className="am-trades__list">
        {range(6).map((i) => (
          <li className="am-trades__item" key={i}>
            <Skeleton w="22px" h={22} radius={5} />
            <div className="am-trades__id">
              <Skeleton w="62px" h={12} />
              <Skeleton w="min(124px, 90%)" h={11} />
            </div>
            <Skeleton w="68px" h={11} />
          </li>
        ))}
      </ul>

      <footer className="am-trades__foot">
        <Skeleton w="88px" h={11} />
      </footer>
    </div>
  );
}

/** Trading objectives — 1:9268: the pass ring, its figures, and two loss rails. */
export function ObjectivesSkeleton() {
  return (
    <section className="am-card am-objectives" aria-busy="true" aria-label={t('Loading objectives')}>
      <header className="am-card__head">
        <span className="am-card__title">
          <Skeleton w="14px" h={14} />
          <Skeleton w="116px" h={13} />
        </span>
        <div className="am-objectives__state">
          <Skeleton w="72px" h={20} pill />
          <Skeleton w="104px" h={11} />
        </div>
      </header>

      <div className="am-objectives__body">
        <div className="am-objectives__target">
          <Skeleton w="88px" h={88} pill />
          <div className="am-objectives__figures">
            <Skeleton w="76px" h={11} />
            <Skeleton w="148px" h={26} />
            <Skeleton w="min(180px, 90%)" h={11} />
          </div>
        </div>

        {range(4).map((i) => (
          <Fragment key={i}>
            {/* The divider before the account-wide rules, as the card draws it. */}
            {i === 2 && <div className="am-objectives__divider" />}
            <div className="am-limit">
              <div className="am-limit__head">
                <Skeleton w="96px" h={11} />
                <Skeleton w="72px" h={11} />
              </div>
              <Skeleton w="100%" h={5} pill />
              <div className="am-limit__notes">
                <Skeleton w="100%" h={10} />
                {i === 0 && (
                  <>
                    <Skeleton w="100%" h={10} />
                    <Skeleton w="100%" h={10} />
                    <Skeleton w="100%" h={10} />
                  </>
                )}
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </section>
  );
}

/** Statistics — the twelve cells of 1:9320, in the card's own 4-column grid. */
export function StatsSkeleton() {
  return (
    <div className="am-card am-stats" aria-busy="true" aria-label={t('Loading statistics')}>
      <HeadSkeleton right="112px" />

      <dl className="am-stats__grid">
        {range(12).map((i) => (
          <div className="am-stats__cell" key={i}>
            <dt className="am-stats__label">
              <Skeleton w="min(84px, 90%)" h={11} />
            </dt>
            <dd className="am-stats__value">
              <Skeleton w="min(64px, 70%)" h={14} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Daily performance — 1:9397.
 *
 * Six week rows: the tallest month `date-fns` lays out, so the row never grows
 * under the statistics card beside it as the real calendar arrives. The card
 * is its own component's markup (`daily`), not the page's, because that is
 * what occupies this half of the row.
 *
 * The Month/Day tabs and the month stepper are bars rather than live controls:
 * a month the trader can step before there are trades in it would be claiming
 * a state the card can't honour yet.
 */
export function DailySkeleton() {
  return (
    <section className="daily" aria-busy="true" aria-label={t('Loading daily performance')}>
      <header className="daily__head">
        <span className="daily__title">
          <Skeleton w="14px" h={14} />
          <Skeleton w="116px" h={13} />
        </span>
        <div className="daily__controls">
          <Skeleton w="104px" h={32} />
          <Skeleton w="124px" h={28} />
        </div>
      </header>

      <div className="daily__body">
        <div className="daily__weekdays">
          {range(7).map((i) => (
            <Skeleton w="100%" h={10} key={i} />
          ))}
        </div>

        <div className="daily__grid">
          {range(42).map((i) => (
            <Skeleton w="100%" h={44} key={i} />
          ))}
        </div>

        <div className="daily__rule" />

        <footer className="daily__foot">
          <div className="daily__foot-left">
            <Skeleton w="min(152px, 70%)" h={12} />
            <Skeleton w="min(184px, 90%)" h={11} />
          </div>
          <Skeleton w="84px" h={14} />
        </footer>
      </div>
    </section>
  );
}

/** Account information — 1:9535: two rows of five fields, split by the rule. */
export function InfoSkeleton() {
  const row = (key) => (
    <div className="am-info__row" key={key}>
      {range(5).map((i) => (
        <div className="am-field" key={i}>
          <Skeleton w="min(88px, 80%)" h={11} />
          <Skeleton w="min(124px, 100%)" h={13} />
        </div>
      ))}
    </div>
  );

  return (
    <section className="am-info" aria-busy="true" aria-label={t('Loading account information')}>
      <HeadSkeleton right="164px" />
      {row('top')}
      {row('bottom')}
    </section>
  );
}
